/*
 * CSS imports MUST come before any component imports.
 * Tailwind v4 uses CSS cascade layers (@layer theme, base, components, utilities).
 * If component-imported SASS files load before tailwind.css, they establish
 * @layer components as the FIRST layer, giving it the LOWEST priority —
 * causing Preflight (@layer base) to override all SASS component styles.
 * By loading tailwind.css first, the correct layer order is established:
 *   theme < base < components < utilities
 */
import './const.sass';
import './layout.sass';
import './tailwind.css';

import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router';

import { MessageProvider, withMessage } from 'ui-common';
import DefaultErrorComponent from 'ui-common/ErrorBoundary/defaultErrorComponent';
import { useProfileState } from 'website/Settings/useProfileData';
import userAuthenticationState from 'logic/auth'
import ChatSasaUserContextBridge from 'components/ChatSasaUserContextBridge'
import { clearChatSasaUserDataWhenReady } from 'utils/chatsasaUserContext'
import useSystemState from './hooks/data/useSystemsState';
import Site from './website';
import DashboardLayout from './website/Layout/DashboardLayout';

import './i18n';

import AppProvider from 'website/AppProvider';

function App(props: Record<string, unknown>) {
	const { t } = useTranslation(['maintenance'])
	const systemState = useSystemState()
	const authState = userAuthenticationState()
	const { profileState } = useProfileState()

	useEffect(() => {
		if (authState.state !== 'unauthenticated') return undefined
		return clearChatSasaUserDataWhenReady()
	}, [authState.state])

	const content =
		systemState === 'active' ? (
			<Router>
				<AppProvider>
					<DashboardLayout>
						<Site {...props} />
					</DashboardLayout>
				</AppProvider>
			</Router>
		) : (
			<div className='MaintainanceMode__Container' data-testid='maintainance-mode-container'>
				<DefaultErrorComponent>
					<p>{t('message')}</p>
				</DefaultErrorComponent>
			</div>
		)

	return (
		<>
			{authState.state === 'authenticated' && (
				<ChatSasaUserContextBridge credentials={authState.credentials} profileState={profileState} />
			)}
			{content}
		</>
	)
}

const AppWithMessage = withMessage(App)

export default function AppContainer() {
	return (
		<Suspense fallback={<div />}>
			<MessageProvider>
				<AppWithMessage />
			</MessageProvider>
		</Suspense>
	)
}
